// src/components/KeyValuesDashboard/components/tabs/OverviewTab.tsx

import { TrendingUp, TrendingDown, Minus, DollarSign, MousePointerClick, Eye, Target, BarChart3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';

import type { ComparisonResponse } from '@/types/analytics';
import {
    formatCurrency,
    formatNumber,
    formatPercentage,
    formatChange,
    getMetricTotal,
    getMetricAverage,
} from '@/utils/keyvalues-helpers';

interface OverviewTabProps {
    data: ComparisonResponse;
    mode: 'all' | 'compare';
    selectedKey: string;
}

export function OverviewTab({ data, selectedKey }: OverviewTabProps) {
    const { summary, values_performance, comparison, metadata } = data;

    // Preparar dados para o gráfico de barras
    const chartData = values_performance.map((vp, index) => ({
        id: `value-${index}`, // Adiciona ID único para cada item
        name: vp.custom_value.length > 15
            ? vp.custom_value.substring(0, 15) + '...'
            : vp.custom_value,
        fullName: vp.custom_value,
        revenue: getMetricTotal(vp.metrics, 'revenue'),
        impressions: getMetricTotal(vp.metrics, 'impressions'),
        clicks: getMetricTotal(vp.metrics, 'clicks'),
        ecpm: getMetricTotal(vp.metrics, 'ecpm'),
    })).slice(0, 10); // Top 10


    return (
        <div className="space-y-6">

            {/* Métricas Gerais */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricCard
                    icon={DollarSign}
                    label="Receita Total"
                    value={formatCurrency(getMetricTotal(summary, 'revenue'))}
                    change={comparison?.changes?.revenue?.percentage}
                    trend={comparison?.changes?.revenue?.trend}
                    color="indigo"
                />

                <MetricCard
                    icon={Eye}
                    label="Impressões"
                    value={formatNumber(getMetricTotal(summary, 'impressions'))}
                    change={comparison?.changes?.impressions?.percentage}
                    trend={comparison?.changes?.impressions?.trend}
                    color="violet"
                />

                <MetricCard
                    icon={MousePointerClick}
                    label="Cliques"
                    value={formatNumber(getMetricTotal(summary, 'clicks'))}
                    change={comparison?.changes?.clicks?.percentage}
                    trend={comparison?.changes?.clicks?.trend}
                    color="purple"
                />

                <MetricCard
                    icon={Target}
                    label="CTR Médio"
                    value={formatPercentage(getMetricAverage(summary, 'ctr'))}
                    change={comparison?.changes?.ctr?.percentage}
                    trend={comparison?.changes?.ctr?.trend}
                    color="pink"
                />
            </div>

            {/* Informações do Período */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 p-6">
                <div className="flex flex-wrap items-center gap-6 text-sm">
                    <div>
                        <span className="text-slate-600">Período:</span>
                        <span className="ml-2 font-semibold text-slate-900">
              {data.period.start} até {data.period.end}
            </span>
                    </div>

                    <div className="h-6 w-px bg-slate-300" />

                    <div>
                        <span className="text-slate-600">Custom Key:</span>
                        <span className="ml-2 font-semibold text-indigo-600">{selectedKey}</span>
                    </div>

                    <div className="h-6 w-px bg-slate-300" />

                    <div>
                        <span className="text-slate-600">Valores Analisados:</span>
                        <span className="ml-2 font-semibold text-slate-900">{metadata.total_values_analyzed}</span>
                    </div>

                    <div className="h-6 w-px bg-slate-300" />

                    <div>
                        <span className="text-slate-600">Total de Registros:</span>
                        <span className="ml-2 font-semibold text-slate-900">{formatNumber(metadata.total_records)}</span>
                    </div>
                </div>
            </div>

            {/* Gráfico de Barras - Top Performers */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-indigo-600" />
                    Top 10 Valores por Receita
                </h3>

                <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis
                            dataKey="id"
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            tick={{ fontSize: 12, fill: '#64748b' }}
                            tickFormatter={(value, index) => chartData[index]?.name || value}
                        />
                        <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
                        <RechartsTooltip
                            content={({ active, payload }) => {
                                if (active && payload && payload.length) {
                                    const data = payload[0].payload;
                                    return (
                                        <div className="bg-white p-4 rounded-xl shadow-xl border border-slate-200">
                                            <p className="font-semibold text-slate-900 mb-2">{data.fullName}</p>
                                            <div className="space-y-1 text-sm">
                                                <p className="text-slate-600">
                                                    Receita: <span className="font-semibold text-indigo-600">{formatCurrency(data.revenue)}</span>
                                                </p>
                                                <p className="text-slate-600">
                                                    Impressões: <span className="font-semibold">{formatNumber(data.impressions)}</span>
                                                </p>
                                                <p className="text-slate-600">
                                                    Cliques: <span className="font-semibold">{formatNumber(data.clicks)}</span>
                                                </p>
                                            </div>
                                        </div>
                                    );
                                }
                                return null;
                            }}
                        />
                        <Bar dataKey="revenue" fill="url(#colorRevenue)" radius={[8, 8, 0, 0]} />
                        <defs>
                            <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#6366f1" stopOpacity={0.8} />
                                <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.6} />
                            </linearGradient>
                        </defs>
                    </BarChart>
                </ResponsiveContainer>
            </div>

            {/* Tabela de Performance Detalhada */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 overflow-hidden">
                <div className="p-6 border-b border-slate-200">
                    <h3 className="text-lg font-bold text-slate-900">
                        Performance Detalhada de Todos os Valores
                    </h3>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                                Valor
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                                Receita
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                                Impressões
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                                Cliques
                            </th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200">
                        {values_performance.map((vp, index) => (
                            <tr key={vp.custom_value} className="hover:bg-slate-50 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center gap-3">
                                        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-100 to-purple-100 text-indigo-600 font-semibold text-sm">
                                            {index + 1}
                                        </div>
                                        <span className="font-medium text-slate-900">{vp.custom_value}</span>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right font-semibold text-slate-900">
                                    {formatCurrency(getMetricTotal(vp.metrics, 'revenue'))}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-slate-600">
                                    {formatNumber(getMetricTotal(vp.metrics, 'impressions'))}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-slate-600">
                                    {formatNumber(getMetricTotal(vp.metrics, 'clicks'))}
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

// Componente de Card de Métrica
interface MetricCardProps {
    icon: any;
    label: string;
    value: string;
    change?: number;
    trend?: 'up' | 'down' | 'stable';
    color: 'indigo' | 'violet' | 'purple' | 'pink';
}

function MetricCard({ icon: Icon, label, value, change, trend, color }: MetricCardProps) {
    const colorClasses = {
        indigo: 'from-indigo-500 to-indigo-600',
        violet: 'from-violet-500 to-violet-600',
        purple: 'from-purple-500 to-purple-600',
        pink: 'from-pink-500 to-pink-600',
    };

    const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus;

    return (
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 p-6 hover:shadow-xl transition-shadow">
            <div className="flex items-start justify-between mb-4">
                <div className={`p-3 rounded-xl bg-gradient-to-br ${colorClasses[color]} shadow-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                </div>

                {change !== undefined && trend && (
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-lg ${
                        trend === 'up' ? 'bg-emerald-50 text-emerald-600' :
                            trend === 'down' ? 'bg-rose-50 text-rose-600' :
                                'bg-slate-50 text-slate-600'
                    }`}>
                        <TrendIcon className="w-3 h-3" />
                        <span className="text-xs font-semibold">{formatChange(change)}</span>
                    </div>
                )}
            </div>

            <p className="text-sm text-slate-600 mb-2">{label}</p>
            <p className="text-2xl font-bold text-slate-900">{value}</p>
        </div>
    );
}